/* eslint-disable no-restricted-imports */
import {CardEnum, CardStateUnordered} from '@colonist/game-states'
import {Placement} from '@floating-ui/react'
import React, {useRef, useState, useMemo, useEffect} from 'react'
import {StackDirection, UIGameCardStack} from '../UIGameCardStack/UIGameCardStack'
import clsx from 'clsx'
import styles from './UIGameCardInventory.module.scss'
import {CardDataController} from '@colonist/game-card-types'
import {UIGameCardViewInventory} from '../../../../../code/client/shared/scripts/ui_game/ui_game_card/UIGameCardViewInventory'
import {CardValidatorUnordered} from '@colonist/game-mechanic-validators'
import {ArrayUtils, clamp} from '@colonist/utils'
import {CARD_RATIO, getIfCardHasBadgeFromType} from '@colonist/ui-game'
import {TranslationObject} from '@colonist/i18n'
import {CustomTooltip} from '../UIGameCard/UIGameCard'

export interface UIGameCardInventoryProps {
    cardState: CardStateUnordered
    cardStackSizes?: CardStateUnordered
    cardsToDisable?: CardStateUnordered
    cardsToGreyOut?: CardStateUnordered
    cardsToHighlight?: CardEnum[]
    cardsToSuggestion?: CardEnum[]
    onClickCard?: (cardEnum: CardEnum) => void
    className?: string
    showTipTitle?: boolean
    showTipBody?: boolean
    tooltipPosition?: Placement
    enablePartition?: boolean
    forceShowCardBadges?: boolean
    animate?: boolean
    bankTradeRatiosState?: {[key in CardEnum]?: number} | undefined
    cardsToScaleOnHover?: CardEnum[]
    cardsHelperText?: {[key in CardEnum]?: TranslationObject} | undefined
    cardSize?: 'xxxs' | 'xxs' | 'xs' | 'sm' | 'base'
    cardStackDirection?: StackDirection
    id?: string
    cardsToShowTooltip?: CardEnum[] | undefined
    cardTooltipOverrides?: {[key in CardEnum]?: CustomTooltip}
    cardsToHideBadge?: CardEnum[]
}

function getTotalWidthOfCards(numberOfStacks: number,
                              widthCausedByUniqueCards: number,
                              numberOfSameType: number,
                              cardGapBetweenSameType: number,
                              cardGapBetweenTypes: number,
                              numberOfPartitions: number,
                              cardWidth: number,
                              partitionWidth: number) {
    const widthCausedBySimilarStackedCards = numberOfSameType * (cardWidth + cardGapBetweenSameType)
    const widthCausedByPartition = numberOfPartitions * (partitionWidth + cardGapBetweenTypes)
    const widthCausedByGapBetweenStacks = (numberOfStacks - 1) * cardGapBetweenTypes

    const totalWidthOfCards = widthCausedByUniqueCards + widthCausedBySimilarStackedCards + widthCausedByGapBetweenStacks + widthCausedByPartition
    return totalWidthOfCards
}

function recomputeGapsForVerticalStacking(cardWidth: number,
                                          cardHeight: number,
                                          numberOfStacks: number,
                                          partitionCount: number,
                                          partitionWidth: number,
                                          containerWidth: number,
                                          defaultGapBetweenStacks: number,
                                          verticalOverlapRatio: number,
                                          setCardGaps: (verticalGap: number, horizontalGap: number) => void,) {
    const verticalGapWithinStack = -cardHeight * verticalOverlapRatio

    const widthForPartitions = partitionCount * (partitionWidth + defaultGapBetweenStacks)
    const widthForAllStacks = numberOfStacks * cardWidth
    const widthForDefaultGaps = (numberOfStacks - 1) * defaultGapBetweenStacks
    const totalWidthNeeded = widthForAllStacks + widthForDefaultGaps + widthForPartitions

    const stacksFitWithoutOverlap = totalWidthNeeded <= containerWidth
    if(stacksFitWithoutOverlap) {
        return setCardGaps(verticalGapWithinStack, defaultGapBetweenStacks)
    }

    const widthAvailableForGaps = containerWidth - widthForAllStacks - widthForPartitions
    const numberOfGapsBetweenStacks = numberOfStacks - 1

    const horizontalGapBetweenStacks = numberOfGapsBetweenStacks > 0
        ? widthAvailableForGaps / numberOfGapsBetweenStacks
        : defaultGapBetweenStacks

    setCardGaps(verticalGapWithinStack, horizontalGapBetweenStacks)
}

function recomputeGapsForHorizontalStacking(defaultGapBetweenSameType: number,
                                            defaultGapBetweenTypes: number,
                                            partitionWidth: number,
                                            numberOfStacks: number,
                                            partitionCount: number,
                                            cardWidth: number,
                                            inventoryWidth: number,
                                            totalNumberOfCards: number,
                                            setCardGaps: (cardGapBetweenSameType: number, cardGapBetweenTypes: number) => void) {
    let nextCardGapBetweenSameType = defaultGapBetweenSameType
    let nextCardGapBetweenTypes = defaultGapBetweenTypes

    const numberOfSameType = totalNumberOfCards - numberOfStacks
    const widthCausedByPartition = partitionCount * partitionWidth
    const widthCausedByHavingUniqueCards = numberOfStacks * cardWidth

    const totalWidthOfCardsInInventory = getTotalWidthOfCards(numberOfStacks,
        widthCausedByHavingUniqueCards,
        numberOfSameType,
        nextCardGapBetweenSameType,
        nextCardGapBetweenTypes,
        partitionCount,
        cardWidth,
        partitionWidth)

    const areCardsWithinContainerBounds = totalWidthOfCardsInInventory <= inventoryWidth
    if(areCardsWithinContainerBounds) return setCardGaps(nextCardGapBetweenSameType, nextCardGapBetweenTypes)

    const pixelOffsetForAllStacks = (numberOfStacks - 1) * 1

    const widthCausedByAllCards = totalNumberOfCards * cardWidth
    const widthCausedByGapBetweenTypes = ((numberOfStacks - 1) + partitionCount) * nextCardGapBetweenTypes
    const totalWidthDedicatedToCardsOfSameType = inventoryWidth - widthCausedByAllCards - widthCausedByPartition - widthCausedByGapBetweenTypes

    nextCardGapBetweenSameType = (totalWidthDedicatedToCardsOfSameType - pixelOffsetForAllStacks) / numberOfSameType
    nextCardGapBetweenSameType = clamp(nextCardGapBetweenSameType, -cardWidth, defaultGapBetweenSameType)

    const totalWidthOfCardsAfterGapApplied = getTotalWidthOfCards(numberOfStacks,
        widthCausedByHavingUniqueCards,
        numberOfSameType,
        nextCardGapBetweenSameType,
        nextCardGapBetweenTypes,
        partitionCount,
        cardWidth,
        partitionWidth)

    const widthPixelTolerance = 0.5
    const doCardsOfDifferentTypeNeedToOverlap = totalWidthOfCardsAfterGapApplied > inventoryWidth + widthPixelTolerance
    if(!doCardsOfDifferentTypeNeedToOverlap) return setCardGaps(nextCardGapBetweenSameType, nextCardGapBetweenTypes)

    const totalWidthDedicatedToCardsOfDifferentType = inventoryWidth - widthCausedByHavingUniqueCards - widthCausedByPartition
    const numberOfGapsBetweenTypes = numberOfStacks - partitionCount

    nextCardGapBetweenTypes = (totalWidthDedicatedToCardsOfDifferentType) / numberOfGapsBetweenTypes

    setCardGaps(nextCardGapBetweenSameType, nextCardGapBetweenTypes)
}

export const UIGameCardInventory = ({
    cardState,
    cardStackSizes = cardState,
    cardsToDisable,
    cardsToGreyOut,
    cardsToHighlight,
    cardsToSuggestion,
    onClickCard,
    className,
    showTipTitle = true,
    showTipBody = true,
    tooltipPosition = 'top',
    enablePartition = true,
    forceShowCardBadges,
    animate = true,
    bankTradeRatiosState,
    cardsToScaleOnHover,
    cardsHelperText,
    cardSize = 'base',
    cardStackDirection = 'horizontal',
    id,
    cardsToShowTooltip,
    cardTooltipOverrides,
    cardsToHideBadge,
}: UIGameCardInventoryProps) => {
    const DEFAULT_GAP_BETWEEN_STACKS = 0.1
    const CARD_ASPECT_RATIO = 1 / CARD_RATIO
    const PARTITION_WIDTH = 6
    const VERTICAL_OVERLAP_RATIO = 0.9
    const HORIZONTAL_OVERLAP_RATIO = 0.8

    const isVerticalDirection = cardStackDirection === 'vertical'

    const {cardValidator, uniqueCardEnums, partitionIndexes} = useMemo(() => {
        const cardValidatorUnordered = new CardValidatorUnordered(cardState)
        const uniqueCards = ArrayUtils.removeDuplicatesFromArray(cardValidatorUnordered.cards)

        const getDisplayOrder = (cardEnum: CardEnum): number => {
            if(cardEnum === CardEnum.Paper) return CardEnum.Cloth
            if(cardEnum === CardEnum.Cloth) return CardEnum.Coin
            if(cardEnum === CardEnum.Coin) return CardEnum.Paper

            // TODO: This is a quick hack. CardEnums should be updated with game states migration
            if(cardEnum >= CardEnum.ProgressCardScienceBack && cardEnum <= CardEnum.ProgressCardScienceSmith) return cardEnum - 17
            if(cardEnum >= CardEnum.ProgressCardTradeBack && cardEnum <= CardEnum.ProgressCardTradeCommodityMonopoly) return cardEnum + 11
            if(cardEnum >= CardEnum.ProgressCardPoliticsBack && cardEnum <= CardEnum.ProgressCardPoliticsWedding) return cardEnum + 11

            return cardEnum
        }

        const sortedUniqueCards = uniqueCards.sort((a, b) => getDisplayOrder(a) - getDisplayOrder(b))

        const uniqueCardDatas = sortedUniqueCards.map((cardEnum) => CardDataController.getCardDataForCard(cardEnum))

        const filteredCardDatas = uniqueCardDatas.filter(((cardData) => cardData != undefined))
        const partitionIndexesToReturn = UIGameCardViewInventory.getPartitionCardIndexes(filteredCardDatas)

        return {cardValidator: cardValidatorUnordered, uniqueCardEnums: sortedUniqueCards, partitionIndexes: partitionIndexesToReturn}
    }, [cardState])

    const [gapWithinStack, setGapWithinStack] = useState(-26)
    const [gapBetweenStacks, setGapBetweenStacks] = useState(DEFAULT_GAP_BETWEEN_STACKS)
    const [naturalWidth, setNaturalWidth] = useState<number | undefined>(undefined)

    const containerRef = useRef<HTMLDivElement>(null)

    const setCardGaps = (nextGapWithinStack: number, nextGapBetweenStacks: number) => {
        setGapWithinStack(nextGapWithinStack)
        setGapBetweenStacks(nextGapBetweenStacks)
    }

    useEffect(() => {
        const element = containerRef.current
        if(!element) return
        const sample = element.firstElementChild
        if(!sample) return

        const recomputeCallback = () => {
            const inventoryDocumentWidth = element.getBoundingClientRect().width
            const pixelCorrection = 1
            const inventoryWidth = inventoryDocumentWidth - pixelCorrection

            const totalNumberOfCards = cardValidator.getNumberOfCardsInInventory()
            const numberOfStacks = uniqueCardEnums.length
            const partitionCount = enablePartition ? partitionIndexes.length : 0

            if(isVerticalDirection) {
                const cardElement = sample.querySelector('[data-card-enum]')
                if(!cardElement) return
                const cardRect = cardElement.getBoundingClientRect()

                const widthForPartitions = partitionCount * (PARTITION_WIDTH + DEFAULT_GAP_BETWEEN_STACKS)
                const calculatedNaturalWidth
                    = (numberOfStacks * cardRect.width)
                    + ((numberOfStacks - 1) * DEFAULT_GAP_BETWEEN_STACKS)
                    + widthForPartitions
                setNaturalWidth(calculatedNaturalWidth)

                recomputeGapsForVerticalStacking(cardRect.width,
                    cardRect.height,
                    numberOfStacks,
                    partitionCount,
                    PARTITION_WIDTH,
                    inventoryWidth,
                    DEFAULT_GAP_BETWEEN_STACKS,
                    VERTICAL_OVERLAP_RATIO,
                    setCardGaps)
                return
            }

            const sampleHeight = sample.getBoundingClientRect().height
            const cardWidth = sampleHeight * CARD_ASPECT_RATIO
            const defaultGapBetweenSameType = -cardWidth * HORIZONTAL_OVERLAP_RATIO

            recomputeGapsForHorizontalStacking(defaultGapBetweenSameType,
                DEFAULT_GAP_BETWEEN_STACKS,
                PARTITION_WIDTH,
                numberOfStacks,
                partitionCount,
                cardWidth,
                inventoryWidth,
                totalNumberOfCards,
                setCardGaps)
        }
        recomputeCallback()

        const resizeObserver = new ResizeObserver(() => recomputeCallback())
        resizeObserver.observe(element)

        return () => resizeObserver.disconnect()
    }, [cardState, partitionIndexes, enablePartition, uniqueCardEnums.length, isVerticalDirection])

    const containerStyle = {
        '--outer-gap': `${gapBetweenStacks}px`,
        ...(naturalWidth !== undefined && {'--natural-width': `${naturalWidth}px`}),
    } as React.CSSProperties

    return (
        <div
            id={id}
            className={clsx(styles.container, {[styles.vertical]: isVerticalDirection}, className)}
            ref={containerRef}
            style={containerStyle}
        >
            {uniqueCardEnums.map((cardEnum, index) => {
                const count = cardValidator.getCardCountForCard(cardEnum)
                const numberOfCardsToDisable = cardsToDisable?.[cardEnum] ?? 0
                const numberOfCardsToGreyOut = cardsToGreyOut?.[cardEnum] ?? 0
                const numberOfCardsToHighlight = cardsToHighlight?.includes(cardEnum) ? count : 0
                const numberOfCardsToSuggestion = cardsToSuggestion?.includes(cardEnum) ? count : 0
                const shouldShowPartition = enablePartition && partitionIndexes.includes(index)
                const shouldScaleOnHover = cardsToScaleOnHover?.includes(cardEnum) ?? false
                const helperText = cardsHelperText?.[cardEnum]
                const cardTypeEnum = CardDataController.getCardTypeFromCardEnum(cardEnum)
                if(cardTypeEnum == undefined) return

                const tradeRatio = bankTradeRatiosState?.[cardEnum]

                const isCardBadgeHidden = cardsToHideBadge?.includes(cardEnum) ?? false
                const cardHasBadge = getIfCardHasBadgeFromType(cardTypeEnum, count)
                const shouldShowBadge = isCardBadgeHidden
                    ? false
                    : forceShowCardBadges === undefined
                        ? cardHasBadge
                        : forceShowCardBadges

                const shouldShowTooltipForThisCard = cardsToShowTooltip === undefined
                    ? true
                    : cardsToShowTooltip.includes(cardEnum)
                const showTipTitleForCard = showTipTitle && shouldShowTooltipForThisCard
                const showTipBodyForCard = showTipBody && shouldShowTooltipForThisCard

                return (
                    <React.Fragment key={cardEnum}>
                        {shouldShowPartition && <div className={clsx(styles.partition, styles[cardSize])} />}
                        <UIGameCardStack
                            cardEnum={cardEnum}
                            count={count}
                            stackMax={cardStackSizes?.[cardEnum]}
                            gap={gapWithinStack}
                            hideBadge={!shouldShowBadge}
                            {...(onClickCard && {onClick: onClickCard})}
                            showTipBody={showTipBodyForCard}
                            showTipTitle={showTipTitleForCard}
                            stackDirection={cardStackDirection}
                            tooltipPosition={tooltipPosition}
                            animate={animate}
                            numberOfCardsToDisable={numberOfCardsToDisable}
                            numberOfCardsToGreyOut={numberOfCardsToGreyOut}
                            tradeRatio={tradeRatio}
                            numberOfCardsToHighlight={numberOfCardsToHighlight}
                            numberOfCardsToSuggestion={numberOfCardsToSuggestion}
                            className={styles.stackWrapper}
                            zIndex={uniqueCardEnums.length - index}
                            scaleOnHover={shouldScaleOnHover}
                            helperText={helperText}
                            size={cardSize}
                            {...(cardTooltipOverrides?.[cardEnum] && {customTooltip: cardTooltipOverrides[cardEnum]})}
                        />
                    </React.Fragment>)
            })}
        </div>
    )
}
